
import React, { useEffect } from 'react';
import { useTransactions } from '@/hooks/useTransactions';
import TransactionDataTable from './transactions/TransactionDataTable';
import { 
  TransactionLoadingState, 
  TransactionErrorState, 
  TransactionEmptyState 
} from './transactions/TransactionEmptyStates';

interface TransactionListProps {
  filterStatus?: string;
  paymentMethodFilter?: string | null;
  keepMoreFilter?: string | null;
  minAmountFilter?: number;
  searchQuery?: string;
  stripeConnected: boolean;
}

const TransactionList: React.FC<TransactionListProps> = ({ 
  filterStatus,
  paymentMethodFilter,
  keepMoreFilter,
  minAmountFilter,
  searchQuery,
  stripeConnected
}) => {
  const { 
    transactions, 
    loading,
    isFetchingFresh,
    error, 
    hasMore, 
    loadMore,
    filterByStatus,
    filterByPaymentMethod,
    filterByKeepMore,
    filterByMinAmount,
    hasStripeAccount,
    hasTransactions,
    refreshTransactions
  } = useTransactions(25, filterStatus);

  // Set up filters based on props
  useEffect(() => {
    filterByStatus(filterStatus);
  }, [filterStatus, filterByStatus]);

  useEffect(() => {
    filterByPaymentMethod(paymentMethodFilter);
  }, [paymentMethodFilter, filterByPaymentMethod]);

  useEffect(() => {
    filterByKeepMore(keepMoreFilter);
  }, [keepMoreFilter, filterByKeepMore]);

  useEffect(() => {
    filterByMinAmount(minAmountFilter);
  }, [minAmountFilter, filterByMinAmount]);

  // First, check if there's no Stripe account connected
  if (!stripeConnected) {
    return <TransactionEmptyState reason="no-account" />;
  }
  
  // Then check for errors
  if (error) {
    console.error('[TransactionList] Error loading transactions:', error);
    return <TransactionErrorState error={error} onRetry={() => loadMore()} />;
  }
  
  // Only show loading state if we're actively loading AND we don't have any transactions yet
  if (loading && !transactions.length && !hasTransactions) {
    return <TransactionLoadingState />;
  }
  
  // Show empty state if we have no visible transactions after filtering
  if (!transactions.length) {
    return (
      <TransactionEmptyState 
        reason={hasTransactions ? 'no-filtered-data' : 'no-data'} 
      />
    );
  }
  
  return (
    <>
      {isFetchingFresh && (
        <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
          <div className="animate-spin mr-2">
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
          </div>
          Updating...
        </div>
      )}
      <TransactionDataTable
        transactions={transactions}
        loading={loading}
        hasMore={hasMore}
        loadMore={loadMore}
      />
    </>
  );
}

export default TransactionList;
